import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output, ViewEncapsulation } from '@angular/core';
import { ButtonComponent } from '@workbuddy/ui/components/button';
import { Command } from '@workbuddy/ui/model';

export type AlertSeverity = 'primary' | 'neutral' | 'info' | 'success' | 'warning' | 'danger';
export type AlertStyle = 'standard' | 'toast';
export type AlertOrientation = 'horizontal' | 'vertical';

/** @description transform primeng severity to workbuddy alert-severity */
function transformAlertSeverity(value: string): AlertSeverity {
    let safeValue = value?.trim()?.toLowerCase();
    const defaultValue: AlertSeverity = 'neutral';

    if (!safeValue) return defaultValue;

    switch (safeValue) {
        case 'error':
            safeValue = 'danger';
            break;
        case 'warn':
            safeValue = 'warning';
            break;
    }

    const alertSeveritySet = new Set<AlertSeverity>(['primary', 'neutral', 'info', 'success', 'warning', 'danger']);
    const isAlertSeverity = alertSeveritySet.has(safeValue as AlertSeverity);

    return isAlertSeverity
        ? safeValue as AlertSeverity
        : defaultValue;
}

@Component({
    selector: 'w-alert',
    templateUrl: 'alert.component.html',
    styleUrls: ['alert.component.less'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        ButtonComponent,
    ],
    host: {
        '[class.w-alert-neutral]': '$severity() === "neutral" || !$severity()',
        '[class.w-alert-primary]': '$severity() === "primary"',
        '[class.w-alert-info]': '$severity() === "info"',
        '[class.w-alert-success]': '$severity() === "success"',
        '[class.w-alert-warning]': '$severity() === "warning"',
        '[class.w-alert-danger]': '$severity() === "danger"',

        '[class.w-alert-standard]': '$displayStyle() === "standard" || !$displayStyle()',
        '[class.w-alert-toast]': '$displayStyle() === "toast"',

        '[class.w-alert-horizontal]': '$orientation() === "horizontal" || !$orientation()',
        '[class.w-alert-vertical]': '$orientation() === "vertical"',
    }
})
export class AlertComponent {

    public $severity = input('neutral', { alias: 'severity', transform: transformAlertSeverity });
    public $icon = input<string>(undefined, { alias: 'icon' });
    public $summary = input<string>(undefined, { alias: 'summary' });
    public $detail = input<string>(undefined, { alias: 'detail' });
    public $closable = input<boolean>(true, { alias: 'closable' });
    public $showIcon = input<boolean>(true, { alias: 'showIcon' });

    public $displayStyle = input<AlertStyle>('standard', { alias: 'displayStyle' });
    public $orientation = input<AlertOrientation>('vertical', { alias: 'orientation' });
    public $actions = input<Command[]>([], { alias: 'actions' });

    public close = output<Event>();


    public $destroyed = linkedSignal(() => {
        const summary = this.$summary();
        const detail = this.$detail();

        return !summary && !detail;
    })


    protected $iconEffective = computed(() => {

        const showIcon = this.$showIcon();
        if (!showIcon) {
            return null;
        }

        const icon = this.$icon();
        if (icon) return icon;

        switch (this.$severity()) {
            case 'success':
                return 'fa-regular fa-circle-check';
            case 'warning':
                return 'fa-regular fa-circle-exclamation';
            case 'danger':
                return 'fa-regular fa-circle-x';
            default:
                return 'fa-regular fa-circle-info';
        }
    })


    constructor() { }


    public onClose(event: Event) {
        this.close.emit(event);
        if (this.$displayStyle() === 'toast') return;
        this.$destroyed.set(true);
    }


}